/* eslint-disable import/order -- the `react-semantic-ui-datepickers` stylesheet is a
   SIDE-EFFECT import, and the ordering this rule asks for moves module imports across
   it. Stylesheet order is cascade order, so that is a change to what the page renders,
   not to its types. */
import React, { useState, useEffect } from 'react';
import type { RejectionWithMessage } from '../types';
import {
  Message,
  Comment,
  Loader,
  Dropdown,
  Button,
  Popup,
  Segment,
  Divider,
  List,
  Icon,
  Confirm,
  Dimmer,
} from 'semantic-ui-react';

import 'react-semantic-ui-datepickers/dist/react-semantic-ui-datepickers.css';
import type { SemanticCOLORS, SemanticICONS, TextAreaProps } from 'semantic-ui-react';
import type { Option, TaskNote } from 'types';

import { useAuthContext } from '../../../contexts/AuthContext';
import * as FHIRUtils from '../../../utils/FHIRUtils';

import moment from 'moment';
import FHIRUpdater from 'data/FHIRUpdater';
import { FHIRProxyClient, ProvenanceBuilder } from 'fhir-sdoh';
import type { Annotation, Provenance, Task } from 'fhir/r4';

import type { ResourceEntry } from '../../../utils/bundleNarrowing';
import { downloadNotesPdf, sanitizePdfFileName } from '../../../utils/pdfDownload';
import type { PdfNoteItem } from '../../../utils/pdfDownload';

/**
 * An entry of the "add note to task" dropdown listing this patient's Tasks.
 *
 * Unlike a plain {@link Option}, `text` is the rendered `List.Item` built by
 * `setDropdown`, not a string.
 */
type TaskOption = {
  key: string;
  value: string;
  text: React.ReactNode;
  id: string;
};

function TasksNotesList(props: {
  // notes/tabs.tsx passes `props.tasksData?.entry`, so this really can be
  // absent — and the `props.notesResponse && props.notesResponse.length` guard
  // in the effect below is what handles that. `removeNote` reads it
  // without a guard, but only runs from a row that exists because the effect
  // already populated `tableData` from a non-empty response.
  notesResponse: ResourceEntry<'Task'>[] | undefined;
  org: string;
  patientId: string;
  refreshTasksNotes: () => void;
  // notes/tabs.tsx renders this component only for the 'TASKS_NOTES' tab.
  type: 'TASKS_NOTES';
  // Supplied by notes/tabs.tsx but never read in this component.
  allReferralData?: unknown;
  practitionerData?: unknown;
  setNotesResponse?: unknown;
}) {
  const authInfo = useAuthContext();
  const practitionerName = authInfo.practitionerName;

  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  // The producer spreads a `TaskNote[]` into an object literal, so the keys are
  // the array's stringified indices ("0", "1", ...) and the values are notes.
  const [tableData, setTableData] = useState<Record<string, TaskNote>>({});
  const [allTableData, setAllTableData] = useState<Record<string, TaskNote>>({});
  const [sortTableData, setSortTableData] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [buttonLoader, setButtonLoader] = useState(false);
  const [_tasks, setTasks] = useState<TaskOption[]>([{ key: '', value: '', text: '', id: '' }]);
  const [selectedTask, _setSelectedTask] = useState('');
  // Keyed by Task id; the values are the raw Task resources, re-sent by
  // `_handleSubmit` after pushing a note onto `payLoad.note`.
  const [availableTasks, setAvailableTasks] = useState<Record<string, Task>>({});
  const [filterNoteTypes, setFilterNoteTypes] = useState<Option[]>([]);
  const [loaderActive, setLoaderActive] = useState(false);
  // Holds the key of the task note pending deletion, or null.
  const [open, setOpen] = useState<string | null>(null);

  const taskIconMaps = {
    touchpoint: 'american sign language interpreting',
    survey: 'write',
    'food-card': 'vcard',
    'food-box': 'box',
    'food-delivery': 'food',
    'other-goods-delivery': 'box',
    education: 'book',
    voicemail: 'phone volume',
    consultation: 'call',
    careplan: 'calendar alternate outline',
    communication: 'phone',
    'food-recall': 'food',
  };

  const taskIconColors = {
    careplan: 'pink',
    communication: 'red',
    consultation: 'yellow',
    education: 'violet',
    voicemail: 'red',
    'food-box': 'brown',
    'food-card': 'red',
    'food-delivery': 'green',
    'other-goods-delivery': 'green',
    survey: 'purple',
    touchpoint: 'orange',
    'food-recall': 'blue',
  };

  // See the SUSPECTED DEFECT note at the assignment below: every value written
  // here is `undefined`, because `TaskNote` has no `child`.
  const sortedChildNotes: Record<string, undefined> = {};

  useEffect(() => {
    setTableData({});
    setAllTableData({});
    if (props.notesResponse && props.notesResponse.length) {
      establishParentChildRelationship(props.notesResponse);
    }
  }, [props.notesResponse, props.type]);

  useEffect(() => {
    setValidationErrors([]);
  }, []);

  const _buttonContent = (buttonLoader && <Loader active inline size="small" />) || (!buttonLoader && 'Add');

  const _handleChange = (_e: React.ChangeEvent<HTMLTextAreaElement>, { name: _name, value }: TextAreaProps) =>
    setNoteText(value as string);

  const _handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // SUSPECTED DEFECT (this file, `_handleSubmit`): `Annotation.time` is a FHIR
    // `dateTime` string, but this builds it as a `moment` object and pushes it
    // straight onto `Task.note`, so the resource sent to the server carries a
    // serialised moment rather than an ISO string. The handler is currently
    // unreferenced (note the `_` prefix), so nothing exercises it.
    // REPORTED, NOT FIXED: calling `.toISOString()` would change what is sent.
    const noteObject = {
      authorString: practitionerName,
      text: noteText,
      time: moment(),
    } as unknown as Annotation;
    setButtonLoader(true);
    // `availableTasks[selectedTask]` is `Task | undefined`, and `Task.note` is
    // `0..*`; both are dereferenced unguarded and throw when absent.
    const payLoad = availableTasks[selectedTask] as Task;
    (payLoad.note as Annotation[]).push(noteObject);

    FHIRUpdater('/Task/' + selectedTask, authInfo.token, payLoad).then(
      () => {
        props.refreshTasksNotes();
        setButtonLoader(false);
      },
      () => {
        props.refreshTasksNotes();
      }
    );
    setNoteText('');
  };

  const buildPdfNotes = (): PdfNoteItem[] => {
    const sortedKeys = Object.keys(tableData).sort(function (a, b) {
      // `tableData[x]` is `TaskNote | undefined` under noUncheckedIndexedAccess,
      // and `time` is itself `0..1` on the source Annotation. Both are read
      // unguarded — `new Date(undefined)` is Invalid Date, so `getTime()`
      // is NaN and the comparison keeps the incoming order.
      const timeOf = (note: TaskNote | undefined) => new Date((note as TaskNote).time as string).getTime();
      if (!sortTableData) {
        return timeOf(tableData[b]) - timeOf(tableData[a]);
      } else {
        return timeOf(tableData[a]) - timeOf(tableData[b]);
      }
    });

    return sortedKeys.map((key) => {
      const note = tableData[key];
      return {
        heading: note?.text || 'Task Note',
        body: `${note?.description || ''}${note?.reasonCode ? ' / ' + note.reasonCode : ''}`,
        meta: `${note?.author ? 'Added by ' + note.author + '. ' : ''}${
          note?.time ? moment(note.time).format('MM-DD-YYYY') : ''
        }`,
      };
    });
  };

  const handleDownloadPdf = () => {
    try {
      setValidationErrors([]);

      const notes = buildPdfNotes();

      const fileName = `${sanitizePdfFileName(props?.type || 'tasks_notes')}_${sanitizePdfFileName(
        props?.patientId || 'patient'
      )}.pdf`;

      downloadNotesPdf({
        title: 'Tasks Notes',
        patientId: props?.patientId,
        notes,
        fileName,
      });
      logNotesDownload(props?.type, notes, 'Tasks notes have been downloaded');
      // A catch variable may only be annotated `any` or `unknown`, so the shape this
      // handler reads is stated by a cast at each read — a `const` alias for the
      // narrowed value would add an emitted binding.
    } catch (error: unknown) {
      console.error(error);
      // Kept on one line: the wrapped form emits a trailing comma into the array
      // literal, which is a change to the emitted program.
      // prettier-ignore
      setValidationErrors([`Failed to download PDF: ${(error as RejectionWithMessage)?.message || (error as { toString(): string })?.toString()}`]);
    }
  };

  const setDropdown = (id: string, reasonCode: string | undefined, description: string) => {
    return (
      <List.Item key={id} as="span">
        <List.Content>
          <List.Description>{description}</List.Description>
          <List.Description>{reasonCode ? reasonCode.split('|')[0] : reasonCode}</List.Description>
        </List.Content>
      </List.Item>
    );
  };

  const establishParentChildRelationship = (notesData: ResourceEntry<'Task'>[]) => {
    const notesList: TaskNote[] = [];
    const tasksList: TaskOption[] = [];
    const taskPayloads: Record<string, Task> = {};
    const filter: Option[] = [{ text: 'All', key: 'ALL', value: 'ALL' }];

    notesData.forEach((task: ResourceEntry<'Task'>) => {
      const resource = task.resource;
      const fhirNote = FHIRUtils.parseFHIRTask(resource);

      if (fhirNote.description && fhirNote.id && fhirNote.reasonCode) {
        taskPayloads[fhirNote.id] = resource;
        tasksList.push({
          text: setDropdown(fhirNote.id, fhirNote.reasonCode, fhirNote.description),
          key: fhirNote.id,
          value: fhirNote.id,
          id: fhirNote.id,
        });
      }

      /* eslint-disable-next-line @typescript-eslint/no-unused-expressions -- short-circuit used as a statement; the rule's only fix is an `if`, which rewrites the expression rather than its type */
      resource.note &&
        resource.note.forEach((taskNote: Annotation, index: number) => {
          const note: TaskNote = {
            author: taskNote.authorString,
            text: taskNote.text,
            time: taskNote.time,
            description: fhirNote.description,
            reasonCode: fhirNote.reasonCode,
            code: fhirNote.code,
            taskId: resource.id,
            taskNoteIndex: index,
          };
          notesList.push(note);
          filter.push({ text: fhirNote.reasonCode, key: fhirNote.reasonCode, value: fhirNote.reasonCode });
        });
    });

    const uniqueFilter = filter.filter((v, i, a) => a.findIndex((v2) => JSON.stringify(v2) === JSON.stringify(v)) === i);
    setFilterNoteTypes(uniqueFilter);

    const sortedNotesList = notesList.sort(function (a, b) {
      // `time` is `0..1`; comparing `undefined` with `>` is always false.
      return (a.time as string) > (b.time as string) ? 1 : -1;
    });

    setTasks(tasksList);

    setAvailableTasks((prevState) => ({
      ...prevState,
      ...taskPayloads,
    }));

    // `{ ...anArray }` copies the array's own enumerable properties, i.e. its
    // indices as string keys ("0", "1", ...) — which is what `Object.keys` over
    // `tableData` relies on — but the spread's static type is the array's, not a
    // Record. `Object.entries` enumerates the same own indices in the same order.
    const notesByIndex: Record<string, TaskNote> = Object.fromEntries(Object.entries(sortedNotesList));

    setTableData((prevState) => ({
      ...prevState,
      ...notesByIndex,
    }));

    setAllTableData((prevState) => ({
      ...prevState,
      ...notesByIndex,
    }));
  };

  const setValidationMessages = () => {
    return validationErrors.map((error) => {
      return <Message.Item key={error}>{error}</Message.Item>;
    });
  };

  const logNotesDownload = (taskId: string, notes: PdfNoteItem[], message: string) => {
    const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
    const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);

    const provenance: Provenance = ProvenanceBuilder.build(
      `Patient/${props.patientId}`,
      { reference: 'Practitioner/' + authInfo.practitionerId, display: practitionerName },
      props.patientId,
      'Task Notes Download',
      message,
      { code: props.org.toUpperCase(), display: props.org.toLowerCase() }
    );

    const fhirBundle = ProvenanceBuilder.buildProvenanceBundle([], provenance);

    fhirProxyClient.send('post', '', fhirBundle).then(
      () => {
        console.log('Task notes download provenance logged');
      },
      (error: { toString(): string }) => {
        console.log('Unable to create provenance: ' + error.toString());
      }
    );
  };

  // Every call site passes the string key of a `tableData` entry (`setOpen(key)`
  // at the Remove button, relayed through `open`). `tableData` is keyed by the
  // stringified array index, so the value arriving here is a string.
  const removeNote = (noteIndex: string) => {
    setOpen(null);
    setValidationErrors([]);

    // `props.notesResponse`, the `find` result and `tableData[noteIndex]` are
    // each dereferenced unguarded and throw for an absent response or a note
    // that is not in it. The assertions preserve all three throws.
    const note = tableData[noteIndex] as TaskNote;
    const taskPayload: fhir4.Task = (
      (props.notesResponse as ResourceEntry<'Task'>[]).find((item) => item.resource.id === note.taskId) as {
        resource: fhir4.Task;
      }
    ).resource;

    const removedNote = (taskPayload.note as Annotation[]).splice(note.taskNoteIndex, 1);

    const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
    const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);

    setLoaderActive(true);

    fhirProxyClient.send('put', `Task/${taskPayload.id}`, taskPayload).then(
      () => {
        const provenance: Provenance = ProvenanceBuilder.build(
          `Task/${taskPayload.id}`,
          { reference: 'Practitioner/' + authInfo.practitionerId, display: practitionerName },
          props.patientId,
          'UPDATE',
          // `splice` returns `[]` when the index is out of range, so `[0]` is
          // `Annotation | undefined`, and `.text` is read off it unguarded.
          `Task note (${(removedNote[0] as Annotation).text}) has been removed`,
          { code: props.org.toUpperCase(), display: props.org.toLowerCase() }
        );

        const fhirBundle = ProvenanceBuilder.buildProvenanceBundle([], provenance);

        fhirProxyClient.send('post', '', fhirBundle).then(
          () => {
            props.refreshTasksNotes();
            setLoaderActive(false);
          },
          (error: { toString(): string }) => {
            setLoaderActive(false);
            setValidationErrors((state) => [...state, 'Unable to create provenance: ' + error.toString()]);
          }
        );
      },
      (error: { toString(): string }) => {
        setLoaderActive(false);
        setValidationErrors((state) => [...state, 'Unable to remove task note: ' + error.toString()]);
      }
    );
  };

  const displayNoteThread = (key: string, tableData: Record<string, TaskNote>) => {
    // The reads below that carry no `?.` dereference `tableData[key]` unguarded;
    // the assertion preserves that.
    const note = tableData[key] as TaskNote;
    // `taskIconMaps` and `taskIconColors` are closed literal maps and `code` is
    // an open string off the Task, so a code outside the twelve listed yields
    // `undefined` — which semantic-ui's `Icon` receives as an absent name/color.
    // The assertions state the current lookup; they do not add a fallback.
    const iconCode = note.code as keyof typeof taskIconMaps;
    return (
      <Comment key={key} style={{ marginTop: '10px' }}>
        <Comment.Content>
          <Icon size="large" name={taskIconMaps[iconCode] as SemanticICONS} color={taskIconColors[iconCode] as SemanticCOLORS}></Icon>

          <Comment.Author as="a" style={{ cursor: 'default', marginLeft: '7px' }}>
            {tableData[key]?.text}
          </Comment.Author>

          <Comment.Metadata>
            <Button compact size="mini" color="red" content="Remove" onClick={() => setOpen(key)} />
          </Comment.Metadata>

          <Comment.Actions style={{ marginLeft: '35px' }}>
            <Comment.Text>
              <div className="noteattr_display_text">
                <span className="note_type_text">{note.description}</span>
                {'/'}
                <span className="note_type_text">{note.reasonCode}</span>{' '}
                {note.author && 'added by'} {note.author}.{' '}
                {note.time && moment(note.time).format('MM-DD-YYYY')}
              </div>
            </Comment.Text>
          </Comment.Actions>
        </Comment.Content>
      </Comment>
    );
  };

  const sortedParentNotes = Object.keys(tableData).sort(function (a, b) {
    // `tableData[x]` is `TaskNote | undefined` under noUncheckedIndexedAccess,
    // and `time` is itself `0..1`. Both are read unguarded —
    // `new Date(undefined)` is Invalid Date, so `getTime()` is NaN and the
    // comparison keeps the incoming order.
    const timeOf = (note: TaskNote | undefined) => new Date((note as TaskNote).time as string).getTime();
    if (!sortTableData) {
      return timeOf(tableData[b]) - timeOf(tableData[a]);
    } else {
      return timeOf(tableData[a]) - timeOf(tableData[b]);
    }
  });

  // SUSPECTED DEFECT (apps/care-betty/src/components/patient/notes/tasksNotesList.tsx:75, 405)
  // `sortedChildNotes` is populated from `tableData[parentKey].child`, but this
  // component's producer (`establishParentChildRelationship`) builds `TaskNote`
  // values, which have no `child` property — unlike notes/list.tsx and
  // notes/listAll.tsx, whose producers really do assemble a `child` array.
  // Typing `tableData` made the compiler say so directly:
  //   "Property 'child' does not exist on type 'TaskNote'".
  // Every entry therefore resolves to `undefined`, and nothing in this file ever
  // reads `sortedChildNotes` afterwards, so the loop is inert today. The same
  // dead pairing exists in notes/connectNotesList.tsx:96-106.
  // REPORTED, NOT FIXED: deleting it, or adding `child` to the producer, would
  // both be behaviour changes. Typed as `Record<string, undefined>` to state what
  // it actually holds; the `as` on the read preserves the unguarded access.
  sortedParentNotes.map(
    (parentKey) => (sortedChildNotes[parentKey] = (tableData[parentKey] as TaskNote & { child?: undefined }).child)
  );

  const filterNotes = (e: React.MouseEvent<HTMLDivElement>) => {
    // `e.target` is typed `EventTarget`; this handler is bound to a Dropdown.Menu
    // whose click target is always a DOM node, so `textContent` is read off it
    // directly.
    const clickedText = (e.target as Node).textContent;
    const filteredTableData: Record<string, TaskNote> = {};
    if (clickedText === 'All' || clickedText === '') {
      setTableData(allTableData);
    } else {
      for (const key in allTableData) {
        // `for...in` only yields keys that exist, so this entry is present.
        const entry = allTableData[key] as TaskNote;
        if (entry.reasonCode === clickedText) {
          filteredTableData[key] = entry;
        }
      }
      setTableData(filteredTableData);
    }
  };

  const sortNotes = () => {
    setSortTableData(!sortTableData);
  };

  return (
    <div>
      <Dimmer active={loaderActive} inverted>
        <Loader></Loader>
      </Dimmer>

      {validationErrors.length > 0 && (
        <Message error>
          <Message.Header>Error:</Message.Header>
          <Message.List>{setValidationMessages()}</Message.List>
        </Message>
      )}

      {/*
        SUSPECTED DEFECT (this file, the `Confirm` below): `open` holds the *key*
        of the task note pending deletion (set by the Remove button), but
        `Confirm.open` is declared `boolean`. semantic-ui reads it for truthiness,
        so the dialog does open — the mismatch is latent, not visible.
        `removeNote(open)` genuinely needs the key, so the two uses of `open`
        disagree about its type and only a behaviour change could reconcile them.
        The same shape exists in notes/listAll.tsx. REPORTED, NOT FIXED — the value
        passed to `open` is left exactly as it was. The cast is what states the
        mismatch; a `Boolean()` would change what semantic-ui receives.
      */}
      <Confirm
        open={open as unknown as boolean}
        onCancel={() => setOpen(null)}
        onConfirm={() => removeNote(open as string)}
      />

      <Segment>
        {filterNoteTypes.length > 0 && (
          <>
            <div className="cb_height_30">
              <span className="cb_floatleft">
                <Popup
                  content="Sort"
                  trigger={<Button circular onClick={sortNotes} name="all" icon="sort" color="teal" />}
                />
                <Dropdown icon="filter" floating button className="icon">
                  <Dropdown.Menu>
                    <Dropdown.Header icon="filter" content="Filter by Type" />
                    <Dropdown.Divider />
                    <Dropdown.Menu onClick={(e: React.MouseEvent<HTMLDivElement>) => filterNotes(e)} scrolling>
                      {/* React's `key` arrives through the spread, which carries
                          `option.key`. An explicit `key={option.value}` used to sit
                          before the spread and was overwritten by it, so it never
                          took effect; both hold `fhirNote.reasonCode`. */}
                      {filterNoteTypes.map((option) => (
                        <Dropdown.Item id={option.value} {...option} style={{ textTransform: 'Capitalize' }} />
                      ))}
                    </Dropdown.Menu>
                  </Dropdown.Menu>
                </Dropdown>
                <Popup
                  content="Refresh Notes"
                  trigger={
                    <Button circular onClick={props.refreshTasksNotes} name="refresh" icon="refresh" color="orange" />
                  }
                />
                <Popup
                  content="Download PDF"
                  trigger={<Button circular onClick={handleDownloadPdf} icon="download" color="green" />}
                />
              </span>
            </div>
            <Divider />
          </>
        )}

        <Comment.Group style={{ minHeight: '300px' }}>
          {sortedParentNotes &&
            sortedParentNotes?.length > 0 &&
            sortedParentNotes.map((key) => displayNoteThread(key, tableData))}
        </Comment.Group>
      </Segment>
    </div>
  );
}

export default TasksNotesList;
